import { forwardRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { MarkdownDialogPayload, MarkdownDialogProps } from './types';
import MarkdownEditable from './MarkdownEditable';
import MarkdownReadOnly from './MarkdownReadOnly';

const MarkdownDialog = forwardRef<HTMLDivElement, MarkdownDialogProps>(
  (props, ref) => {
    const {
      initialMarkdown = '',
      onSave,
      title,
      isEditable = true,
      cancelButtonText = isEditable ? 'Cancel' : 'Close',
      saveButtonText = 'Save',
      ...otherProps
    }: MarkdownDialogPayload = props.payload;

    const { open, onClose } = props;

    const [updatedMarkdown, setUpdatedMarkdown] = useState(initialMarkdown);

    const handleSave = async () => {
      if (onSave) {
        const isSaved = await onSave(updatedMarkdown);
        if (isSaved) onClose();
      }
    };

    const handleCancel = () => {
      onClose();
    };

    return (
      <Dialog
        ref={ref}
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="md"
        {...otherProps}
      >
        {title && <DialogTitle>{title}</DialogTitle>}

        {isEditable ? (
          <MarkdownEditable
            markdown={updatedMarkdown}
            setUpdatedMarkdown={setUpdatedMarkdown}
          />
        ) : (
          <MarkdownReadOnly markdown={updatedMarkdown} />
        )}

        <DialogActions sx={{ marginBlockStart: '1rem' }}>
          {isEditable ? (
            <>
              <Button
                onClick={handleCancel}
                variant="outlined"
              >
                {cancelButtonText}
              </Button>
              <Button
                onClick={handleSave}
                variant="outlined"
              >
                {saveButtonText}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleCancel}
              variant="outlined"
            >
              {cancelButtonText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);

MarkdownDialog.displayName = 'MarkdownDialog';

export default MarkdownDialog;
